/**
 * Product-page ocean audio player with starfish progress scrubber.
 *
 * @extends {HTMLElement}
 */
class ProductPreviewAudio extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #dragging = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const audio = this.querySelector('audio');
    const playButton = this.querySelector('.product-preview-audio__play');
    const track = this.querySelector('[data-track]');
    const starfish = this.querySelector('[data-starfish]');

    if (!(audio instanceof HTMLAudioElement) || !(playButton instanceof HTMLButtonElement) || !(track instanceof HTMLElement)) {
      return;
    }

    playButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.toggle();
      },
      { signal }
    );

    audio.addEventListener('timeupdate', () => this.#syncProgress(), { signal });
    audio.addEventListener('loadedmetadata', () => this.#syncProgress(), { signal });
    audio.addEventListener(
      'ended',
      () => {
        this.#setPlaying(false);
        audio.currentTime = 0;
        this.#syncProgress();
      },
      { signal }
    );

    const seekFromEvent = (/** @type {PointerEvent} */ event) => {
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        audio.currentTime = ratio * audio.duration;
        this.#syncProgress();
      }
    };

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event instanceof PointerEvent)) return;
        this.#dragging = true;
        track.setPointerCapture?.(event.pointerId);
        seekFromEvent(event);
      },
      { signal }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!this.#dragging || !(event instanceof PointerEvent)) return;
        seekFromEvent(event);
      },
      { signal }
    );

    const endDrag = (/** @type {PointerEvent} */ event) => {
      if (!this.#dragging) return;
      this.#dragging = false;
      track.releasePointerCapture?.(event.pointerId);
    };

    track.addEventListener('pointerup', endDrag, { signal });
    track.addEventListener('pointercancel', endDrag, { signal });

    if (starfish instanceof HTMLElement) {
      starfish.addEventListener(
        'keydown',
        (event) => {
          if (!(event instanceof KeyboardEvent) || !Number.isFinite(audio.duration)) return;
          const step = audio.duration / 20;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            audio.currentTime = Math.min(audio.duration, audio.currentTime + step);
            this.#syncProgress();
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            audio.currentTime = Math.max(0, audio.currentTime - step);
            this.#syncProgress();
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  toggle() {
    const audio = this.querySelector('audio');
    if (!(audio instanceof HTMLAudioElement)) return;

    if (this.hasAttribute('data-playing') && !audio.paused) {
      audio.pause();
      this.#setPlaying(false);
      return;
    }

    const playPromise = audio.play();
    this.#setPlaying(true);
    if (playPromise !== undefined) {
      playPromise.catch(() => this.#setPlaying(false));
    }
  }

  /**
   * @param {boolean} playing
   */
  #setPlaying(playing) {
    const playButton = this.querySelector('.product-preview-audio__play');
    if (!(playButton instanceof HTMLButtonElement)) return;

    if (playing) {
      this.setAttribute('data-playing', 'true');
      playButton.setAttribute('aria-label', playButton.dataset.pauseLabel || 'Pause');
    } else {
      this.removeAttribute('data-playing');
      playButton.setAttribute('aria-label', playButton.dataset.playLabel || 'Play');
    }
  }

  #syncProgress() {
    const audio = this.querySelector('audio');
    const fill = this.querySelector('[data-fill]');
    const starfish = this.querySelector('[data-starfish]');
    const currentEl = this.querySelector('[data-current]');
    const durationEl = this.querySelector('[data-duration]');

    if (!(audio instanceof HTMLAudioElement)) return;

    const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
    const ratio = duration ? audio.currentTime / duration : 0;
    const progress = `${(ratio * 100).toFixed(3)}%`;

    this.style.setProperty('--progress', progress);
    if (fill instanceof HTMLElement) fill.style.setProperty('--progress', progress);
    if (starfish instanceof HTMLElement) starfish.style.setProperty('--progress', progress);
    if (currentEl) currentEl.textContent = this.#formatTime(audio.currentTime || 0);
    if (durationEl) durationEl.textContent = this.#formatTime(duration);
  }

  /**
   * @param {number} seconds
   */
  #formatTime(seconds) {
    const total = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

if (!customElements.get('product-preview-audio')) {
  customElements.define('product-preview-audio', ProductPreviewAudio);
}
